import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, CardContent, FormControlLabel, Skeleton, Switch, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { getEmailPreferences, setEmailPreferences } from "../../Services/notificationsApi";

/**
 * My Account → Email notifications (October 2026). One switch for now: the
 * daily email when home maintenance is coming up or overdue. The same switch
 * the unsubscribe link in that email turns off. Saves on toggle, optimistic,
 * and puts the switch back if the save fails.
 */
const EmailPreferencesCard: React.FC = () => {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [on, setOn] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setState("loading");
    getEmailPreferences()
      .then((p) => {
        setOn(p.maintenanceReminderEmails);
        setState("ready");
      })
      .catch(() => setState("error"));
  }, []);

  useEffect(() => load(), [load]);

  const toggle = async (next: boolean) => {
    if (saving) return;
    setOn(next);
    setSaving(true);
    try {
      const saved = await setEmailPreferences({ maintenanceReminderEmails: next });
      setOn(saved.maintenanceReminderEmails);
      toast.success(saved.maintenanceReminderEmails ? "Maintenance reminder emails are on." : "Maintenance reminder emails are off.");
    } catch {
      setOn(!next);
      toast.error("Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "16px" }} data-testid="email-preferences">
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
          Email notifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Reminders always show in the bell when you're signed in. Choose what we also email you.
        </Typography>

        {state === "loading" && <Skeleton variant="rounded" height={56} data-testid="email-preferences-loading" />}

        {state === "error" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="body2" color="error">
              We couldn't load your email settings.
            </Typography>
            <Button size="small" variant="outlined" onClick={load} sx={{ textTransform: "none" }}>
              Try again
            </Button>
          </Box>
        )}

        {state === "ready" && (
          <FormControlLabel
            sx={{ alignItems: "flex-start", m: 0, gap: 1.5 }}
            control={
              <Switch
                checked={on}
                onChange={(e) => toggle(e.target.checked)}
                disabled={saving}
                inputProps={{ "aria-label": "Maintenance reminder emails" }}
              />
            }
            label={
              <Box sx={{ pt: 0.75 }}>
                <Typography variant="body1" fontWeight={500}>
                  Maintenance reminders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One email when a job on your home's schedule is due within a week, or overdue.
                </Typography>
              </Box>
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default EmailPreferencesCard;
